import type { EmailCategory, EmailSearchResult, EmailSearchWindow } from "../types";
import { useOpenMessage } from "../hooks/useOpenMessage";
import { useOpenOutlookSearch } from "../hooks/useOpenOutlookSearch";
import { PaperclipIcon } from "../../../shared/components/icons";

function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

const CATEGORY_STYLES: Record<EmailCategory, { label: string; bg: string; fg: string; border: string }> = {
  received: { label: "Inbox", bg: "#EFF6FC", fg: "#0F548C", border: "#C7E0F4" },
  sent: { label: "Sent", bg: "#EDF6EE", fg: "#107C10", border: "#BAD7BB" },
  draft: { label: "Draft", bg: "#FFF4CE", fg: "#7A4F01", border: "#F3D27B" },
};

// Display priority: Inbox first, Sent next, Drafts last. Items without a category
// (older payloads) sort to the end before drafts. Array.prototype.sort is stable
// in modern JS, so chronological order within each group is preserved.
const CATEGORY_RANK: Record<EmailCategory, number> = { received: 0, sent: 1, draft: 2 };
function categoryRank(category: EmailCategory | undefined): number {
  if (!category) return 1.5;
  return CATEGORY_RANK[category];
}

function CategoryChip({ category }: { readonly category: EmailCategory }) {
  const s = CATEGORY_STYLES[category];
  return (
    <span
      style={{
        marginLeft: 6,
        fontSize: 10,
        fontWeight: 600,
        padding: "1px 6px",
        borderRadius: 8,
        backgroundColor: s.bg,
        color: s.fg,
        border: `1px solid ${s.border}`,
        verticalAlign: "middle",
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </span>
  );
}

interface EmailResultsListProps {
  readonly querySummary: string;
  readonly results: EmailSearchResult[];
  readonly onItemClick?: (result: EmailSearchResult) => void;
  /** Set on mailbox searches only; absent on calendar cards and reply checks. */
  readonly searchWindow?: EmailSearchWindow;
}

function messageCount(r: EmailSearchResult): number {
  return typeof r.threadCount === "number" && r.threadCount > 1 ? r.threadCount : 1;
}

/**
 * Header count. When the backend collapsed conversations (catch-up), a bare
 * "4 results" silently contradicts Outlook's unread badge of 11 — so name both
 * numbers. Rows that carry no `threadCount` (every other search path) keep the
 * original wording exactly.
 */
function formatResultCount(results: EmailSearchResult[]): string {
  if (results.length === 0) return " — no results found";
  const messages = results.reduce((sum, r) => sum + messageCount(r), 0);
  if (messages > results.length) {
    const threadPlural = results.length === 1 ? "" : "s";
    return ` — ${results.length} thread${threadPlural} · ${messages} messages`;
  }
  const plural = results.length === 1 ? "" : "s";
  return ` — ${results.length} result${plural}`;
}

/**
 * The AIVA-7020 (MS-01 / G-4) disclosure. A search reads only the first N matching
 * messages, and until this line existed a truncated list was indistinguishable
 * from a complete one — "4 results" read as "there are 4" when there might be
 * forty.
 *
 * Two things the sentence deliberately does NOT claim.
 *
 * No total. The specification's copy is "Showing the 8 most relevant of N", but
 * Graph cannot report N on every search path, and pairing the card count with a
 * mailbox-wide total ("4 of 63") would imply 59 withheld results — the cards have
 * already been narrowed by relevance. Naming the window instead is the accepted
 * deviation on AIVA-7020.
 *
 * No ORDER. An earlier version said "the 25 most recent", which is false on the
 * keyword path: `$search` cannot carry a sort order, so Graph returns the best
 * matches in no date order (see list_emails_obo). Sender, subject, date and
 * attachment searches arrive in Outlook's default date order, which we never
 * explicitly request either. One order-free sentence is true on all three paths,
 * and honours §6.1's rule that the search "is query-driven, not exhaustive. Copy
 * must not imply otherwise."
 */
function truncationNotice(searchWindow: EmailSearchWindow | undefined, resultCount: number): string | null {
  if (!searchWindow?.saturated) return null;
  // "emails", not "messages": on an attachment search "25 messages" reads as
  // "25 attachments", and one email can carry several.
  const scope = searchWindow.searched ? `${searchWindow.searched} emails` : "a limited set of emails";
  return resultCount === 0
    ? `Nothing matched in ${scope} — there may be more.`
    : `Searched ${scope} — there may be more.`;
}

/**
 * Whether to offer the hand-off. Shown when the list may be incomplete, and on a
 * no-hits answer — §6.1's empty state points the user at Outlook search for the
 * fuzzy matches AiVA's query-driven search cannot reach. A complete short list
 * needs no escape hatch, so it gets none.
 *
 * A query is required, not just a reason: catch-up searches on unread + a date
 * window with no terms at all, and a button that drops the user into an empty
 * Outlook search box has handed nothing over. That turn still gets the notice.
 */
function showsOutlookHandoff(searchWindow: EmailSearchWindow | undefined, resultCount: number): boolean {
  if (!searchWindow?.outlookQuery) return false;
  return searchWindow.saturated || resultCount === 0;
}

export function EmailResultsList({
  querySummary,
  results,
  onItemClick,
  searchWindow,
}: Readonly<EmailResultsListProps>) {
  const openMessage = useOpenMessage();
  const openOutlookSearch = useOpenOutlookSearch();
  const sortedResults = [...results].sort((a, b) => categoryRank(a.category) - categoryRank(b.category));
  const notice = truncationNotice(searchWindow, results.length);
  const handoff = showsOutlookHandoff(searchWindow, results.length);

  return (
    <div style={{ marginTop: 8 }}>
      <div
        style={{
          fontSize: 12,
          color: "#605E5C",
          marginBottom: 6,
          fontStyle: "italic",
        }}
      >
        {querySummary}{formatResultCount(results)}
      </div>

      {notice && (
        <div
          style={{
            fontSize: 11,
            color: "#605E5C",
            marginBottom: 6,
            padding: "5px 8px",
            background: "#FFF8E1",
            border: "1px solid #F3D27B",
            borderRadius: 4,
          }}
        >
          {notice}
        </div>
      )}

      {sortedResults.map((r) => (
        <button
          type="button"
          key={r.id}
          onClick={() => (onItemClick ? onItemClick(r) : openMessage(r.id, r.webLink))}
          title="Open in Outlook"
          style={{
            display: "block",
            width: "100%",
            textAlign: "left",
            background: "#FAFAFA",
            border: "1px solid #E1DFDD",
            borderRadius: 4,
            padding: "8px 10px",
            marginBottom: 4,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: "#201F1E",
              marginBottom: 2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {r.subject || "(no subject)"}
            {r.category && <CategoryChip category={r.category} />}
            {r.hasAttachments && (
              <span style={{ marginLeft: 6, fontSize: 14, color: "#605E5C", display: "inline-flex" }}>
                <PaperclipIcon />
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "#605E5C", marginBottom: r.preview ? 2 : 0 }}>
            {r.fromName || r.from}
            {r.date && <span style={{ marginLeft: 8 }}>{formatDate(r.date)}</span>}
            {messageCount(r) > 1 && (
              <span style={{ marginLeft: 8 }}>· {r.threadCount} messages</span>
            )}
          </div>
          {r.preview && (
            <div
              style={{
                fontSize: 11,
                color: "#8A8886",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {r.preview}
            </div>
          )}
        </button>
      ))}

      {handoff && (
        <button
          type="button"
          onClick={() => openOutlookSearch(searchWindow?.outlookQuery ?? "")}
          // Labelled for what it does, not for the specification's name for it
          // ("Search in Outlook"): Outlook exposes no way to drive its own search
          // box, so this copies the query for one paste. See useOpenOutlookSearch.
          title="Copy this search so you can paste it into Outlook, which searches your whole mailbox"
          style={{
            display: "block",
            width: "100%",
            textAlign: "center",
            background: "#FFFFFF",
            border: "1px solid #0F548C",
            borderRadius: 4,
            padding: "6px 10px",
            marginTop: 4,
            color: "#0F548C",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Copy search for Outlook
        </button>
      )}
    </div>
  );
}
